import React, { FC, useMemo, useState } from 'react';
import { Lock } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { deviceStatus } from 'api/Responses/DeviceResponses';
import AsyncImage from 'components/AsyncImage/AsyncImage';
import BackButton from 'components/BackButton/BackButton';
import Button from 'components/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import FormControlLabel from 'components/FormControl/FormControlLabel';
import InputField from 'components/InputField';
import Label from 'components/Label/Label';
import Page from 'components/Page';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import PdfPreview from 'components/PdfPreview/PdfPreview';
import EditDraftModal from 'components/SetModal/Workflows/EditDraftModal';
import ViewEnlargedImageModal from 'components/ViewEnlargedImageModal/ViewEnlargedImageModal';
import { useLanguage } from 'context/AppContext';
import useDevice from 'hooks/useDevice';
import { deviceActions } from 'reducers/DeviceReducer/deviceActions';
import { useUser } from 'store/user/useUser';
import { StorageType } from 'utils/helpers/image.helpers';
import * as Styles from './DevicePage.styles';

// Match params on the page
type DevicePageParams = {
  code: string;
};

const DevicePage: FC = () => {
  const { code = '' } = useParams<DevicePageParams>();
  const user = useUser();
  const [language] = useLanguage();
  const navigate = useNavigate();

  const [isEditDraftModalOpen, setIsEditDraftModalOpen] = useState<boolean>(false);
  const [isEnlargedImageModalOpen, setIsEnlargedImageModalOpen] = useState<boolean>(false);

  // Load device on page mount
  const { loading: loadingPage, device, dispatchDevice } = useDevice(code, true, true);

  const isOwnedByMe = useMemo(() => {
    if (!user.isAuthenticated) {
      return;
    }

    return device.owned_by === user.user_id;
  }, [device.owned_by, user]);

  const additionalInspectionText = useMemo(
    () =>
      // Try the new inspection text field
      (device.test_metadata?.additional_inspection_english ||
        // Otherwise try the old inspection text field
        device?.inspection_text) ??
      // If no values are available, keep the value controlled with an empty string (https://reactjs.org/docs/forms.html#controlled-components)
      '',
    [device],
  );

  if (device.draft_exists && isOwnedByMe && device.id) {
    navigate(`/device/${device.id}/edit`);
  }

  return (
    <Page>
      {loadingPage ? (
        <PageSpinner />
      ) : (
        <Styles.Container>
          {/* Header */}
          <Styles.DevicePageRowAlignCenter>
            <Styles.HeaderContainer>
              <BackButton onClick={() => navigate(-1)} />
              <h2>{device.description}</h2>
            </Styles.HeaderContainer>
            <Styles.ButtonContainer>
              <Button
                onClick={() => {
                  setIsEditDraftModalOpen(true);
                }}
                disabled={device.draft_exists}
              >
                {language.DraftActions.create}
              </Button>
            </Styles.ButtonContainer>
          </Styles.DevicePageRowAlignCenter>

          {device.owned_by && (
            <Styles.FormElement width={10}>
              <Label text={language.DevicePage.status}>
                <Styles.Status status={deviceStatus[device.status]}>
                  {!isOwnedByMe && <Lock fontSize="small" />}
                  {language.DeviceStatus[device.status]}
                </Styles.Status>
              </Label>
            </Styles.FormElement>
          )}

          {/* Code */}
          <Styles.FormElement width={20}>
            <Label text={language.DevicePage.code}>
              <InputField
                variant="outlined"
                fullWidth
                value={device.id}
                onChange={e => dispatchDevice(deviceActions.id(e.target.value))}
                disabled={true}
              />
            </Label>
          </Styles.FormElement>

          {/* Number of Parts */}
          <Styles.FormElement width={20}>
            <Label text={language.DevicePage.numParts}>
              <InputField
                type="number"
                variant="outlined"
                fullWidth
                value={device.num_parts}
                onChange={e => dispatchDevice(deviceActions.numParts(e.target.value))}
                disabled={true}
              />
            </Label>
          </Styles.FormElement>

          {/* Description */}
          <Styles.FormElement width={50}>
            <Label text={language.DevicePage.description}>
              <InputField
                variant="outlined"
                fullWidth
                value={device.description}
                onChange={e => dispatchDevice(deviceActions.description(e.target.value))}
                disabled={true}
              />
            </Label>
          </Styles.FormElement>

          {/* Japanese Description */}
          <Styles.FormElement width={50}>
            <Label text={language.DevicePage.japaneseDescription}>
              <InputField
                variant="outlined"
                fullWidth
                value={device.language_metadata?.description_japanese}
                onChange={e => dispatchDevice(deviceActions.japaneseDescription(e.target.value))}
                disabled={true}
              />
            </Label>
          </Styles.FormElement>

          {/* Checkboxes */}
          <Styles.FormCheckboxGroup>
            <Styles.FormElement width={12}>
              {/* Cannulated */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!device.cannulated}
                    onChange={() => dispatchDevice(deviceActions.cannulated(!device.cannulated))}
                    disabled={true}
                  />
                }
                label={language.DevicePage.cannulated}
              />

              {/* Debris Inspection */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!device.test_metadata?.debris_test}
                    onChange={() => dispatchDevice(deviceActions.debrisInspection(!device.test_metadata?.debris_test))}
                    disabled={true}
                  />
                }
                label={language.DeviceTests.debrisInspection}
              />

              {/* Function Test */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!device.test_metadata?.functional_test}
                    onChange={() => dispatchDevice(deviceActions.functionTest(!device.test_metadata?.functional_test))}
                    disabled={true}
                  />
                }
                label={language.DeviceTests.functionTest}
              />

              {/* Torque Test */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!device.test_metadata?.torque_test}
                    onChange={() => dispatchDevice(deviceActions.torqueTest(!device.test_metadata?.torque_test))}
                    disabled={true}
                  />
                }
                label={language.DeviceTests.torqueTest}
              />

              {/* Prone to Debris */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!device.prone_to_debris}
                    onChange={() => dispatchDevice(deviceActions.proneToDebris(!device.prone_to_debris))}
                    disabled={true}
                  />
                }
                label={language.DeviceTests.proneToDebris}
              />
            </Styles.FormElement>
          </Styles.FormCheckboxGroup>

          {/* Additional Inspection */}
          <Styles.FormElement width={50}>
            <Label text={language.DevicePage.additionalInspection}>
              <InputField
                variant="outlined"
                fullWidth
                value={additionalInspectionText}
                onChange={e => dispatchDevice(deviceActions.additionalInspection(e.target.value))}
                disabled={true}
              />
            </Label>
          </Styles.FormElement>

          {/* Japanese Additional Inspection */}
          <Styles.FormElement width={50}>
            <Label text={language.DevicePage.japaneseAdditionalInspection}>
              <InputField
                variant="outlined"
                fullWidth
                value={device.test_metadata?.additional_inspection_japanese}
                onChange={e => dispatchDevice(deviceActions.japaneseAdditionalInspection(e.target.value))}
                disabled={true}
              />
            </Label>
          </Styles.FormElement>

          {/* Image */}
          <Styles.FormElement width={50}>
            <Label text="Part Image">
              <Styles.UploadContainer>
                <AsyncImage
                  alt={device.description}
                  src={device.image}
                  imgWidth={19}
                  imgHeight={16}
                  isEnlargedImageAvailable
                  storageType={StorageType.FIREBASE}
                />
              </Styles.UploadContainer>
            </Label>
          </Styles.FormElement>

          {/* Inspection Manual */}
          <Styles.FormElement width={50}>
            <Label text="Inspection Manual">
              <Styles.UploadContainer>
                <PdfPreview
                  firebase
                  file={device.inspection_manual}
                  deviceId={device.id}
                  fileUrl={device.language_metadata?.manual_english ?? undefined}
                />
              </Styles.UploadContainer>
            </Label>
          </Styles.FormElement>
          <EditDraftModal
            open={isEditDraftModalOpen}
            close={() => setIsEditDraftModalOpen(false)}
            url={`/device/${device.id}/edit`}
            titleText={language.EditDraftModal.deviceTitle}
            bodyText={language.EditDraftModal.deviceMessage}
            type={'DEVICE'}
          />
          <ViewEnlargedImageModal
            modalOpen={isEnlargedImageModalOpen}
            setClosed={() => setIsEnlargedImageModalOpen(false)}
            alt={device.id}
            src={device.image}
          />
        </Styles.Container>
      )}
    </Page>
  );
};

export default DevicePage;
